// src/Pages/BlitzPay/blitzpaypagecomponents/PendingItemsModal.jsx
// v5 — 4-color system:
//   Incoming         → green       (#3B6D11 ramp)
//   BlitzPay         → red         (#E24B4A ramp)
//   Split sent       → amber       (#BA7517 ramp)   (outgoing BBC / SME)
//   Transfer         → blue        (#185FA5 ramp)
//   Incoming + Split → yellow-green (#5E7A0E ramp)  ← spectrum blend of amber + green
import React from "react";
import {
  Dialog, DialogTitle, DialogContent,
  Box, Typography, List, ListItem, ListItemAvatar, ListItemText,
  Avatar, Button, Chip, IconButton, Alert, Divider,
  useMediaQuery, Stack,
} from "@mui/material";
import {
  Warning as WarningIcon,
  Close as CloseIcon,
  Check as CheckIcon,
  ArrowDownward as ArrowDownwardIcon,
  Info as InfoIcon,
  Bolt as BoltIcon,
  Share as ShareIcon,
  SwapHoriz as TransferIcon,
  CallSplit as SplitInIcon,
} from "@mui/icons-material";
import { useTheme, alpha } from "@mui/material/styles";
import { buildPendingItems } from "./utils";

// ── Color tokens ──────────────────────────────────────────────────────── //
const COLORS = {
  incoming: {
    bg:      "#EAF3DE",
    border:  "#C0DD97",
    accent:  "#3B6D11",
    muted:   "#C0DD97",
    text:    "#173404",
    sub:     "#3B6D11",
    btn:     "#3B6D11",
    btnTxt:  "#EAF3DE",
    label:   "#27500A",
    labelBg: "#C0DD97",
  },
  blitzpay: {
    bg:      "#FCEBEB",
    border:  "#F7C1C1",
    accent:  "#E24B4A",
    muted:   "#F7C1C1",
    text:    "#501313",
    sub:     "#A32D2D",
    btn:     "#A32D2D",
    btnTxt:  "#FCEBEB",
    label:   "#791F1F",
    labelBg: "#F7C1C1",
  },
  split: {
    bg:      "#FAEEDA",
    border:  "#FAC775",
    accent:  "#BA7517",
    muted:   "#FAC775",
    text:    "#412402",
    sub:     "#854F0B",
    btn:     "#854F0B",
    btnTxt:  "#FAEEDA",
    label:   "#633806",
    labelBg: "#FAC775",
  },
  transfer: {
    bg:      "#E6F1FB",
    border:  "#B5D4F4",
    accent:  "#185FA5",
    muted:   "#B5D4F4",
    text:    "#042C53",
    sub:     "#185FA5",
    btn:     "transparent",
    btnTxt:  "#185FA5",
    btnBorder:"#378ADD",
    label:   "#0C447C",
    labelBg: "#B5D4F4",
  },
  // Incoming BBC Split — yellow-green: spectrum blend of amber (#BA7517) and green (#3B6D11)
  // Sits perceptually between them: chartreuse-olive warmth with a green lean
  incomingSplit: {
    bg:      "#EDF5D5",   // warm yellow-tinted green surface
    border:  "#CBDF88",   // yellow-green border — between amber's FAC775 and green's C0DD97
    accent:  "#5E7A0E",   // deep yellow-green — halfway on the hue arc
    muted:   "#CBDF88",   // same as border for avatar bg
    text:    "#1E2E04",   // very dark olive for readability
    sub:     "#4A6109",   // muted yellow-green for meta text
    btn:     "#5E7A0E",   // button fill matches accent
    btnTxt:  "#EDF5D5",
    label:   "#3A4C07",
    labelBg: "#CBDF88",
  },
};

// ── Detection helpers ─────────────────────────────────────────────────── //
const isBBCSplit = (item) =>
  item?.invoice_type === "bbc" ||
  item?.source_type  === "bbc" ||
  item?.isBBC;

const isTransfer = (item) =>
  item?.recordType === "transfer" ||
  item?.type === "Internal Transfer";

const isBlitzPay = (item) =>
  (item?.invoice_type === "blitzpay" || item?.source_type === "blitzpay" || item?.isBlitzPay) &&
  !isBBCSplit(item);

const getRowColor = (item) => {
  if (isTransfer(item))                          return COLORS.transfer;
  if (item?.isIncoming && isBBCSplit(item))      return COLORS.incomingSplit; // ← yellow-green blend
  if (isBBCSplit(item))                          return COLORS.split;         // outgoing BBC → amber
  if (isBlitzPay(item))                          return COLORS.blitzpay;
  if (item?.isIncoming)                          return COLORS.incoming;
  return COLORS.blitzpay;
};

const getIcon = (item) => {
  if (isTransfer(item))                          return <TransferIcon />;
  if (item?.isIncoming && isBBCSplit(item))      return <SplitInIcon />;  // distinct icon for incoming split
  if (isBBCSplit(item))                          return <ShareIcon />;
  if (isBlitzPay(item))                          return <BoltIcon />;
  if (item?.isIncoming)                          return <ArrowDownwardIcon />;
  return <BoltIcon />;
};

const getTypeLabel = (item) => {
  if (isTransfer(item))                          return "Transfer";
  if (item?.isIncoming && isBBCSplit(item))      return "Split received";
  if (isBBCSplit(item))                          return "Split sent";
  if (isBlitzPay(item))                          return "BlitzPay";
  if (item?.isIncoming)                          return "Incoming";
  return "BlitzPay";
};

const getCtaLabel = (item, isMobile) => {
  if (isTransfer(item))                          return "View details";
  if (item?.isIncoming && isBBCSplit(item))      return isMobile ? "Review Split" : "Review Split ⚡";
  if (isBBCSplit(item))                          return isMobile ? "Manage" : "Manage Split";
  return isMobile ? "Review" : "Review & approve";
};

const PendingItemsModal = ({
  open,
  onClose,
  pendingInvoices = [],
  incomingInvoices = [],
  formatAmount,
  onItemAction,
  getPaymentName,
}) => {
  const theme    = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const allPendingItems = buildPendingItems({
    pendingInvoices,
    incomingInvoices,
    getPaymentName,
  });

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : 3,
          maxHeight: isMobile ? "100%" : "85vh",
          m: isMobile ? 0 : 2,
          ...(isMobile && {
            paddingTop:    "env(safe-area-inset-top, 0px)",
            paddingBottom: "env(safe-area-inset-bottom, 0px)",
            height:        "100vh",
            maxHeight:     "100vh",
          }),
        },
      }}
    >
      {/* ── Header ──────────────────────────────────────────────────── */}
      <DialogTitle sx={{
        pb: 1, px: isMobile ? 2 : 3,
        ...(isMobile && { pt: "max(16px, env(safe-area-inset-top, 0px))" }),
      }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Avatar sx={{ bgcolor: "warning.main", width: isMobile ? 36 : 40, height: isMobile ? 36 : 40 }}>
              <WarningIcon sx={{ fontSize: isMobile ? 20 : 24 }} />
            </Avatar>
            <Box>
              <Typography variant={isMobile ? "subtitle1" : "h6"} fontWeight={700}>
                Pending invoices
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {allPendingItems.length}{" "}
                {allPendingItems.length === 1 ? "invoice" : "items"} requiring attention
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{
        pt: 2, px: isMobile ? 2 : 3, pb: isMobile ? 2 : 2,
        ...(isMobile && {
          flex: 1, overflow: "auto", WebkitOverflowScrolling: "touch",
          paddingBottom: "calc(16px + env(safe-area-inset-bottom, 0px))",
        }),
      }}>
        {allPendingItems.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 6 }}>
            <CheckIcon sx={{ fontSize: 64, color: "success.main", mb: 2 }} />
            <Typography variant="h6" fontWeight={600} gutterBottom>All caught up!</Typography>
            <Typography variant="body2" color="text.secondary">No pending invoices at this time.</Typography>
          </Box>
        ) : (
          <>
            <Alert severity="info" icon={<InfoIcon />} sx={{
              mb: 2, borderRadius: 2,
              "& .MuiAlert-message": { fontSize: isMobile ? "0.8rem" : "0.875rem" },
            }}>
              Review all pending invoices — incoming require your approval, outgoing are awaiting client action or ready to manage.
            </Alert>

            {/* ── Legend ──────────────────────────────────────────────── */}
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 2 }}>
              {[
                { color: COLORS.incoming.accent,       label: "Incoming" },
                { color: COLORS.blitzpay.accent,       label: "BlitzPay" },
                { color: COLORS.split.accent,          label: "Split sent" },
                { color: COLORS.incomingSplit.accent,  label: "Split received" },
                { color: COLORS.transfer.accent,       label: "Transfer" },
              ].map(({ color, label }) => (
                <Box key={label} sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                  <Box sx={{ width: 10, height: 10, borderRadius: "2px", bgcolor: color }} />
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.72rem" }}>
                    {label}
                  </Typography>
                </Box>
              ))}
            </Box>

            <List sx={{ width: "100%", p: 0, display: "flex", flexDirection: "column", gap: 1 }}>
              {allPendingItems.map((item, index) => {
                const c        = getRowColor(item);
                const ctaLabel = getCtaLabel(item, isMobile);
                const isView   = isTransfer(item);
                const status   = (item.status || "").toLowerCase();
                const subStatus =
                  isBBCSplit(item) && !item.isIncoming
                    ? status === "approved"
                      ? "Client approved · electing payouts"
                      : "Awaiting client approval"
                    : isBBCSplit(item) && item.isIncoming
                    ? "Split payout — review your share"
                    : null;

                return (
                  <ListItem
                    key={`${item.type}-${item.id}`}
                    sx={{
                      borderLeft:   `3px solid ${c.accent}`,
                      borderTop:    `0.5px solid ${c.border}`,
                      borderRight:  `0.5px solid ${c.border}`,
                      borderBottom: `0.5px solid ${c.border}`,
                      borderRadius: "0 8px 8px 0",
                      bgcolor: c.bg,
                      py: isMobile ? 1.5 : 1.5,
                      px: isMobile ? 1.5 : 1.75,
                      flexDirection: isMobile ? "column" : "row",
                      alignItems:    isMobile ? "stretch" : "center",
                      gap: isMobile ? 1.5 : 0,
                      "&:hover": { filter: "brightness(0.97)" },
                      transition: "filter 0.15s ease",
                    }}
                  >
                    {/* Avatar + title */}
                    <Box sx={{ display: "flex", alignItems: "center", width: "100%" }}>
                      <ListItemAvatar sx={{ minWidth: isMobile ? 48 : 52 }}>
                        <Avatar sx={{
                          bgcolor:      c.muted,
                          color:        c.accent,
                          width:        isMobile ? 38 : 36,
                          height:       isMobile ? 38 : 36,
                          borderRadius: "8px",
                        }}>
                          {getIcon(item)}
                        </Avatar>
                      </ListItemAvatar>

                      <ListItemText
                        sx={{ m: 0, flex: 1 }}
                        primary={
                          <Stack spacing={0.5}>
                            <Typography
                              variant="body2"
                              fontWeight={600}
                              sx={{ color: c.text, wordBreak: "break-word", fontSize: "0.88rem" }}
                            >
                              {item.name}
                            </Typography>
                            <Stack direction="row" spacing={0.75} flexWrap="wrap">
                              <Box sx={{
                                display: "inline-flex", alignItems: "center",
                                bgcolor:      c.labelBg,
                                color:        c.label,
                                borderRadius: "4px",
                                px: 0.875, py: "2px",
                                fontSize: "0.68rem", fontWeight: 600,
                              }}>
                                {getTypeLabel(item)}
                              </Box>
                              {subStatus && (
                                <Box sx={{
                                  display: "inline-flex", alignItems: "center",
                                  bgcolor:      "transparent",
                                  color:        c.sub,
                                  border:       `0.5px solid ${c.accent}`,
                                  borderRadius: "4px",
                                  px: 0.875, py: "2px",
                                  fontSize: "0.68rem", fontWeight: 500,
                                }}>
                                  {subStatus}
                                </Box>
                              )}
                            </Stack>
                          </Stack>
                        }
                      />
                    </Box>

                    {/* Amount + meta */}
                    <Box sx={{ width: "100%", pl: isMobile ? 0 : "52px" }}>
                      <Typography
                        variant="body1"
                        fontWeight={600}
                        sx={{ color: c.text, fontVariantNumeric: "tabular-nums", fontSize: "0.95rem" }}
                      >
                        {formatAmount(item.amount)}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ color: c.sub, fontSize: "0.72rem", wordBreak: "break-word" }}
                      >
                        {item.isIncoming && !isBBCSplit(item)
                          ? "From "
                          : isTransfer(item)
                          ? "Internal · "
                          : "To "}
                        <strong>{item.client_name || item.company_name || item.creatorId || "—"}</strong>
                        {item.po_number && item.po_number !== "N/A" ? ` · PO: ${item.po_number}` : ""}
                        {" · "}{new Date(item.date).toLocaleDateString()}
                        {item.payment_term ? ` · Net ${item.payment_term}` : ""}
                      </Typography>
                    </Box>

                    {/* CTA */}
                    <Box sx={{ flexShrink: 0, pl: isMobile ? 0 : 2, mt: isMobile ? 0.5 : 0 }}>
                      <Button
                        fullWidth={isMobile}
                        size="small"
                        variant={isView ? "outlined" : "contained"}
                        onClick={() => onItemAction(item, index)}
                        sx={{
                          bgcolor:  isView ? "transparent" : c.btn,
                          color:    c.btnTxt,
                          border:   isView ? `0.5px solid ${c.btnBorder || c.accent}` : "none",
                          borderRadius: "8px",
                          fontWeight: 600,
                          fontSize: "0.78rem",
                          textTransform: "none",
                          px: 2, py: 0.75,
                          whiteSpace: "nowrap",
                          boxShadow: "none",
                          "&:hover": {
                            bgcolor:   isView ? alpha(c.accent, 0.08) : c.btn,
                            filter:    "brightness(0.9)",
                            boxShadow: "none",
                          },
                        }}
                      >
                        {ctaLabel}
                      </Button>
                    </Box>
                  </ListItem>
                );
              })}
            </List>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default PendingItemsModal;